import { useNavigate } from '@remix-run/react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  answerHeading,
  isGuideBrowse,
  officeSearchHint,
  SEARCH_SUGGESTIONS,
  searchCatalog,
  type SearchAccess,
  type SearchAction,
  type RankedSearchEntry,
} from './global-search-catalog';

interface GlobalSearchProps extends SearchAccess {
  onAction?: (action: SearchAction) => void;
  /** Sidebar copy is full width so it fits under the logo. */
  variant?: 'header' | 'sidebar';
  currentOfficeName?: string;
  officeCount?: number;
}

function Spark({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path
        d="M9 1.4l1.15 3.7a4 4 0 002.75 2.75L16.6 9l-3.7 1.15a4 4 0 00-2.75 2.75L9 16.6l-1.15-3.7a4 4 0 00-2.75-2.75L1.4 9l3.7-1.15a4 4 0 002.75-2.75L9 1.4z"
        stroke="currentColor"
        strokeWidth="1.3"
      />
    </svg>
  );
}

export function GlobalSearch({
  onAction,
  variant = 'header',
  currentOfficeName,
  officeCount = 1,
  mainOfficeId,
  canManageBilling,
  canManageTeamProfiles,
  isSuperAdmin,
}: GlobalSearchProps) {
  const navigate = useNavigate();
  const listId = useId();
  const instanceId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const thinkingTimer = useRef<number | undefined>(undefined);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [thinking, setThinking] = useState(false);

  const results = useMemo(
    () =>
      searchCatalog(query, {
        mainOfficeId,
        canManageBilling,
        canManageTeamProfiles,
        isSuperAdmin,
      }),
    [query, mainOfficeId, canManageBilling, canManageTeamProfiles, isSuperAdmin]
  );

  const trimmed = query.trim();
  const guideBrowse = isGuideBrowse(trimmed);
  const answer =
    trimmed && !thinking && !guideBrowse
      ? results.find(entry => entry.kind === 'howto')
      : undefined;
  const places = (
    guideBrowse
      ? results
      : answer
        ? results.filter(entry => entry.id !== answer.id)
        : results.filter(entry => entry.kind === 'place')
  ).slice(0, guideBrowse ? results.length : 6);
  const rows: RankedSearchEntry[] = answer ? [answer, ...places] : places;

  useEffect(() => {
    return () => window.clearTimeout(thinkingTimer.current);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onOtherSearch = (event: Event) => {
      if ((event as CustomEvent<string>).detail !== instanceId) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('tooth-search-open', onOtherSearch);
    window.dispatchEvent(new CustomEvent('tooth-search-open', { detail: instanceId }));
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('tooth-search-open', onOtherSearch);
    };
  }, [open, instanceId]);

  const hintFor = (entry: RankedSearchEntry) =>
    entry.id === 'howto-other-office'
      ? officeSearchHint(currentOfficeName, officeCount)
      : entry.hint;

  const canFollow = (entry: RankedSearchEntry) => {
    if (entry.action === 'switch-office') return officeCount > 1;
    return Boolean(entry.action || entry.hrefResolved);
  };

  const choose = (entry: RankedSearchEntry | undefined) => {
    if (!entry) return;
    setOpen(false);
    setQuery('');
    setThinking(false);
    if (entry.action) {
      if (canFollow(entry)) onAction?.(entry.action);
      return;
    }
    if (entry.hrefResolved) navigate(entry.hrefResolved);
  };

  const ask = (text: string) => {
    window.clearTimeout(thinkingTimer.current);
    setThinking(false);
    setQuery(text);
    setActiveIndex(0);
    setOpen(true);
  };

  const onChange = (value: string) => {
    setQuery(value);
    setActiveIndex(0);
    setOpen(true);
    window.clearTimeout(thinkingTimer.current);
    if (!value.trim()) {
      setThinking(false);
      return;
    }
    setThinking(true);
    thinkingTimer.current = window.setTimeout(() => setThinking(false), 280);
  };

  const onInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const list = trimmed ? rows : SEARCH_SUGGESTIONS;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex(index => Math.min(index + 1, Math.max(list.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex(index => Math.max(index - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (!trimmed) {
        ask(SEARCH_SUGGESTIONS[activeIndex] ?? SEARCH_SUGGESTIONS[0]);
        return;
      }
      choose(rows[activeIndex] ?? rows[0]);
    } else if (event.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div ref={rootRef} className={variant === 'sidebar' ? 'relative w-full' : 'relative w-80'}>
      <label htmlFor={`${listId}-input`} className="sr-only">
        Ask ToothReferral
      </label>
      <div
        className={`rounded-[14px] p-px ${
          open
            ? 'animate-search-ring bg-[length:220%_220%] bg-gradient-to-r from-purple via-[#3d7bff] to-[#b9a6ff]'
            : 'bg-gradient-to-r from-[#d9d3f5] via-[#d5e2fb] to-[#e7e3f8]'
        }`}
      >
        <div className="flex min-h-[42px] items-center gap-2 rounded-[13px] bg-white px-3">
          <Spark className="h-[18px] w-[18px] shrink-0 text-purple" />
          <input
            id={`${listId}-input`}
            ref={inputRef}
            value={query}
            role="combobox"
            aria-expanded={open}
            aria-controls={listId}
            aria-autocomplete="list"
            placeholder="Ask how to embed, forward, invite..."
            className="w-full bg-transparent py-2.5 text-sm text-gray-900 placeholder:text-[#98a0b3] focus:outline-none"
            onChange={event => onChange(event.target.value)}
            onFocus={() => setOpen(true)}
            onKeyDown={onInputKeyDown}
          />
        </div>
      </div>

      {open && (
        <div
          id={listId}
          role="listbox"
          aria-label="Search results"
          className={`absolute left-0 z-40 mt-2 max-h-[28rem] overflow-y-auto rounded-2xl border border-[#e7eaf3] bg-white shadow-[0_18px_50px_rgba(28,39,68,0.14)] ${
            variant === 'sidebar' ? 'w-[380px]' : 'w-[440px] max-w-[calc(100vw-8rem)]'
          }`}
        >
          {thinking ? (
            <div className="m-2.5 h-16 animate-pulse rounded-xl bg-gradient-to-r from-gray-100 via-purple-100 to-gray-100" />
          ) : (
            <>
              {!trimmed && (
                <>
                  <p className="mx-3.5 mb-0.5 mt-3 text-[11px] font-bold uppercase tracking-wider text-[#8b93a7]">
                    Try asking
                  </p>
                  <div className="flex flex-wrap gap-1.5 px-3 pb-3">
                    {SEARCH_SUGGESTIONS.map((text, index) => (
                      <button
                        key={text}
                        type="button"
                        className={`rounded-full border px-2.5 py-1.5 text-[12.5px] text-slate-700 ${
                          index === activeIndex
                            ? 'border-[#c9bdfc] bg-[#f6f4ff]'
                            : 'border-[#e4e7f2] bg-white hover:border-[#c9bdfc] hover:bg-[#f6f4ff]'
                        }`}
                        onMouseDown={event => event.preventDefault()}
                        onClick={() => ask(text)}
                      >
                        {text}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {answer && (
                <div className="m-2.5 rounded-xl bg-white p-3 shadow-[inset_0_0_0_1px_#efeaff]">
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#5b3de0]">
                    <Spark className="h-3 w-3" />
                    Answer
                  </div>
                  <h3 className="mt-1.5 text-[15px] font-semibold text-gray-900">
                    {answerHeading(answer.title)}
                  </h3>
                  <p className="mt-1 text-[13.5px] leading-snug text-[#3d4860]">
                    {hintFor(answer)}
                  </p>
                  {canFollow(answer) && (
                    <button
                      type="button"
                      className="mt-2.5 rounded-lg bg-[#172033] px-2.5 py-1.5 text-[13px] text-white"
                      onClick={() => choose(answer)}
                    >
                      Open {answer.goLabel ?? answerHeading(answer.title)}
                    </button>
                  )}
                </div>
              )}

              {places.length > 0 && (
                <>
                  <p className="mx-3.5 mb-0.5 mt-1 text-[11px] font-bold uppercase tracking-wider text-[#8b93a7]">
                    {guideBrowse ? 'How-tos' : trimmed ? 'Also in the app' : 'Places'}
                  </p>
                  {places.map((entry, index) => {
                    const rowIndex = answer ? index + 1 : index;
                    const selected = Boolean(trimmed) && rowIndex === activeIndex;
                    return (
                      <button
                        key={entry.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`flex w-full items-start justify-between gap-3 px-3 py-2 text-left ${
                          selected ? 'bg-[#f6f7fb]' : 'hover:bg-[#f6f7fb]'
                        }`}
                        onMouseEnter={() => setActiveIndex(rowIndex)}
                        onClick={() => choose(entry)}
                      >
                        <span>
                          <strong className="block text-sm font-semibold text-gray-900">
                            {entry.title}
                          </strong>
                          <span className="mt-0.5 block text-xs leading-snug text-gray-500">
                            {hintFor(entry) || entry.group}
                          </span>
                        </span>
                        <em className="mt-0.5 shrink-0 rounded-full bg-[#f6f4ff] px-1.5 py-0.5 text-[11px] font-normal not-italic text-[#6d628f]">
                          {entry.kind === 'howto' ? 'How-to' : 'Open'}
                        </em>
                      </button>
                    );
                  })}
                </>
              )}

              {trimmed && results.length === 0 && (
                <p className="px-3.5 py-4 text-[13px] text-gray-500">
                  Nothing in the app matches that. Try “embed form” or “forward a referral”.
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
